<template>
  <AutoComplete
    ref="autoCompleteRef"
    :model-value="internalValue"
    :input-id="id"
    :disabled="disabled"
    :loading="suggestions.loading"
    :suggestions="suggestions.data"
    :option-label="locationOptionLabel"
    :append-to="appendTo"
    :placeholder="t('address.city')"
    :invalid="!!errors.length"
    :pt="{
      pcInputText: {
        root: {
          autofocus: autofocus,
        },
      },
    }"
    :dropdown="!disabled"
    :show-clear="!disabled"
    class="quick-quote-location-quick-search"
    @blur="onAutoCompleteBlur"
    @clear="onClear"
    @complete="onLocationSearch($event.query)"
    @item-select="emit('address-book-select', $event.value)"
    @update:model-value="onItemSelected"
    @value-change="onValueChange"
  >
    <template #option="{ option }">
      <LocationQuickSearchOption :option="option" />
    </template>
  </AutoComplete>
</template>

<script lang="ts" setup>
import { ErrorObject } from '@vuelidate/core';
import { CancelTokenSource, default as axios } from 'axios';
import type { PropType, Ref } from 'vue';
import { ref, toRefs, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable, Location } from '@rygen/rygen-ui/model';

import api from '@/api';

import LocationQuickSearchOption from '@/components/location/LocationQuickSearchOption.vue';
import AutoComplete from 'primevue/autocomplete';

const internalValue = defineModel({
  type: String,
  default: () => undefined,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  errors: {
    type: Array as PropType<ErrorObject[]>,
    default: () => [],
  },
  appendTo: {
    type: String as PropType<'body' | 'self'>,
    default: () => 'self',
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  autofocus: {
    type: Boolean,
    default: () => false,
  },
});
const { id, errors, appendTo, disabled, autofocus } = toRefs(props);
const emit = defineEmits<{
  (event: 'address-book-select', location: Location): void;
  (event: 'clear'): void;
}>();

const autoCompleteRef = useTemplateRef('autoCompleteRef');

const { t } = useI18n();
const toast = useToast();

const suggestions: Ref<Loadable<Location[]>> = ref({ loading: false });
const cancelRequest: Ref<CancelTokenSource | undefined> = ref(undefined);

const locationOptionLabel = (opt: Location | string) =>
  typeof opt === 'string' ? opt : (opt.address?.locality ?? '');

const onLocationSearch = async (query: string) => {
  cancelRequest.value?.cancel();
  cancelRequest.value = axios.CancelToken.source();

  try {
    suggestions.value.loading = true;
    suggestions.value.data = await api.shippingPoint.quickSearch(query, {
      includeTrimble: false,
      cancelToken: cancelRequest.value,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    suggestions.value.error = e;
    console.error('Error loading locations', e);
    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('locations.locations', 2),
      }),
      error: e,
    });
  } finally {
    suggestions.value.loading = false;
  }
};

const onItemSelected = async (query: string | Location) => {
  if (typeof query === 'string') {
    internalValue.value = query;
  } else {
    emit('address-book-select', query);
  }
};

const onClear = () => {
  emit('clear');
};

/**
 * Hack to get around `AutoComplete` not emitting it's `clear` event
 *
 * This function checks if the value is nullish and emits a `clear` event,
 * otherwise it ignores the change.
 */
const onValueChange = (newVal: string) => {
  if (!newVal) {
    onClear();
  }
};

const clearSearchTimeout = () => {
  // Cancel 'complete' callback in autocomplete component
  // This handles the case where the user enters or pastes a value and then
  // quickly clicks or tabs away.
  // Without clearing the timeout, the 'complete' callback will be called
  // which will invoke the search which will then open the dropdown, even though
  // the user is no longer on this component at all.
  // This is totally abusing the internal API of the autocomplete component, I
  // fully expect it to break in the future.
  const searchTimeout = (autoCompleteRef.value as any)?.searchTimeout;
  clearTimeout(searchTimeout);
};

const onAutoCompleteBlur = () => {
  // cancel ongoing http request
  cancelRequest?.value?.cancel();
  clearSearchTimeout();
};
</script>

<style>
.location-quick-search {
  .p-autocomplete-input {
    width: 100%;
  }
}
</style>
